/**
 * Top Navigation Route Configuration
 *
 * Structure: Primary nav sections → Sub-nav items
 * Each primary section maps to a set of sub-navigation tabs shown below the top bar.
 *
 * Route paths are used for matching the active section/tab based on useLocation().
 */

export const topNavSections = [
  {
    key: 'warranty-claims',
    label: 'Warranty Claims',
    defaultPath: '/warranty-claims',
    allowedRoles: [999],
    allowedPermissions: ['warranty_claims.view', 'warranty_claims.create', 'warranty_claims.review', 'warranty_claims.fulfill'],
    subNav: [{ key: 'warranty-claims-list', label: 'Claims', path: '/warranty-claims', icon: 'safety' }]
  },
  {
    key: 'production',
    label: 'Production',
    defaultPath: '/production-plans-v2',
    allowedRoles: [4, 5, 6, 999],
    allowedPermissions: ['production.view', 'production.manage'],
    subNav: [
      { key: 'prod-plans-v2', label: 'Plans V2', path: '/production-plans-v2', icon: 'file' },
      { key: 'prod-smart', label: 'Smart Planner', path: '/smart-production', icon: 'rocket' },
      { key: 'prod-job-cards', label: 'Job Cards', path: '/job-cards', icon: 'tool' },
      { key: 'prod-presets', label: 'Presets', path: '/production-presets', icon: 'setting' },
      { key: 'prod-inv-requests', label: 'Inventory Requests', path: '/inventory-requests', icon: 'database' },
      { key: 'prod-dispatch', label: 'Dispatch to Sales', path: '/dispatch-to-sales', icon: 'truck' },
      { key: 'prod-rejected', label: 'Rejected Stock', path: '/rejected-stock', icon: 'warning' },
      { key: 'prod-discarded', label: 'Discarded Stock', path: '/discarded-stock-management', icon: 'delete' },
      { key: 'prod-user-steps', label: 'User Steps', path: '/user-production-steps', icon: 'team' },
      { key: 'prod-equipment', label: 'Equipment', path: '/equipment-management', icon: 'setting' },
      { key: 'prod-step-mapping', label: 'Step Mapping', path: '/step-position-mapping', icon: 'environment' },
    ]
  },
  {
    key: 'sales',
    label: 'Sales',
    defaultPath: '/admin-dashboard',
    allowedRoles: [3, 4, 5, 999],
    allowedPermissions: ['sales.overview.view'],
    subNav: [
      { key: 'sales-dashboard', label: 'Dashboard', path: '/admin-dashboard', icon: 'dashboard', allowedRoles: [999], allowedPermissions: ['sales.overview.view'] },
      { key: 'sales-daily-entries', label: 'Daily Entries', path: '/admin-daily-entry-dealers', icon: 'file', allowedRoles: [3, 4, 5, 999] },
      { key: 'sales-price-lists', label: 'Price Lists', path: '/price-lists', icon: 'tags', allowedRoles: [3, 4, 5, 999] },
    ]
  },
  {
    key: 'sales-coordination',
    label: 'Sales Coordination',
    defaultPath: '/sales-create-order',
    allowedRoles: [3, 4, 5, 7, 999],
    allowedPermissions: ['warranty.register', 'warranty.manage'],
    subNav: [
      { key: 'sc-create-order', label: 'Create Order', path: '/sales-create-order', icon: 'edit', allowedRoles: [3, 4, 5, 7, 999] },
      { key: 'sc-dispatch', label: 'Dispatch Entries', path: '/sales-dispatch-entries', icon: 'package', allowedRoles: [3, 4, 5, 7, 999] },
      { key: 'sc-pending', label: 'Pending', path: '/sales-pending-entries', icon: 'clock', allowedRoles: [3, 4, 5, 7, 999] },
      { key: 'sc-inprod', label: 'In-Production', path: '/sales-inprod-entries', icon: 'factory', allowedRoles: [3, 4, 5, 7, 999] },
      { key: 'sc-warranty', label: 'Warranty', path: '/dealer-warranty', icon: 'safety', allowedRoles: [3, 4, 5, 999], allowedPermissions: ['warranty.register', 'warranty.manage'] },
      { key: 'sc-pricing', label: 'Pricing Entries', path: '/data-entry-pricing', icon: 'invoice', allowedRoles: [3, 4, 5, 999] },
    ]
  },
  {
    key: 'data-entry',
    label: 'Data Entry',
    defaultPath: '/entry-dashboard',
    allowedRoles: [3, 999],
    subNav: [
      { key: 'de-dashboard', label: 'Dashboard', path: '/entry-dashboard', icon: 'dashboard' },
      { key: 'de-daily-entries', label: 'Daily Entries', path: '/add-daily-entry', icon: 'edit' },
      { key: 'de-inwards', label: 'Inwards Entry', path: '/add-inwards-entry', icon: 'inbox' },
      { key: 'de-dealers', label: 'Dealers', path: '/dealers-list', icon: 'team' },
      { key: 'de-bill-tally', label: 'Physical Bill Tally', path: '/physical-bill-tally', icon: 'file' },
    ]
  },
  {
    key: 'inventory',
    label: 'Inventory',
    defaultPath: '/inventory-locations',
    allowedRoles: [3, 4, 5, 6, 999],
    allowedPermissions: ['inventory.view', 'inventory.manage'],
    subNav: [
      { key: 'inv-locations', label: 'Locations', path: '/inventory-locations', icon: 'environment' },
      { key: 'inv-movements', label: 'Movements', path: '/inventory-movements', icon: 'swap' },
    ]
  },
  {
    key: 'purchase',
    label: 'Purchase',
    defaultPath: '/purchase/requisitions',
    allowedRoles: [5, 8, 9, 10, 999],
    allowedPermissions: ['purchase.view', 'purchase.manage', 'purchase.approve'],
    subNav: [
      { key: 'pur-vendor-purchases', label: 'Vendor Purchases', path: '/purchase/vendor-purchases', icon: 'truck' },
      { key: 'pur-requisitions', label: 'Requisitions', path: '/purchase/requisitions', icon: 'audit' },
      { key: 'pur-submit', label: 'Submit Request', path: '/purchase/requisitions/create', icon: 'form' },
      { key: 'pur-indents', label: 'Indents', path: '/purchase/indents', icon: 'unordered-list' },
      { key: 'pur-po', label: 'Purchase Orders', path: '/purchase/po', icon: 'file' },
      { key: 'pur-grn', label: 'GRN', path: '/purchase/grn', icon: 'inbox' },
      { key: 'pur-items', label: 'Items Master', path: '/purchase/items', icon: 'appstore' },
      { key: 'pur-categories', label: 'Categories', path: '/purchase/item-categories', icon: 'tags' },
      { key: 'pur-molds', label: 'Mold Management', path: '/mold-management', icon: 'tool' },
    ]
  },
  {
    key: 'costing-process',
    label: 'Costing Process',
    defaultPath: '/costing/step-1-opening-stock',
    allowedRoles: [999],
    allowedPermissions: ['costing.view'],
    subNav: [
      { key: 'cost-step1-stock-sync', label: 'Step 1 · Stock Sync', path: '/costing/step-1-opening-stock', icon: 'audit' },
      { key: 'cost-step3-production', label: 'Step 3 · Production', path: '/costing/step-3-production-costing', icon: 'tool' },
      { key: 'cost-step4-july-lineage', label: 'Step 4 · Sales Map', path: '/costing/step-4-july-sales-lineage', icon: 'database' },
      { key: 'cost-source-raw-purchases', label: 'Source · Raw Purchases', path: '/costing/sources/raw-purchases', icon: 'dollar' },
      { key: 'cost-source-fmbk', label: 'Source · FMBK In', path: '/costing/sources/fmbk-inventory-in', icon: 'dollar' },
      { key: 'cost-source-erp-in', label: 'Source · ERP In', path: '/costing/sources/erp-inventory-in', icon: 'dollar' },
      { key: 'cost-source-adjustments', label: 'Source · Adjustments', path: '/costing/sources/adjustments', icon: 'dollar' },
      { key: 'cost-source-restorations', label: 'Source · Restorations', path: '/costing/sources/restorations', icon: 'database' },
      { key: 'cost-source-opening', label: 'Source · Opening', path: '/costing/sources/opening-stock', icon: 'dollar' },
      { key: 'cost-source-production', label: 'Source · Production', path: '/costing/sources/production', icon: 'dollar' },
      { key: 'cost-product-movement-pricing', label: 'Movement Pricing', path: '/costing/product-movement-pricing', icon: 'dollar' },
      { key: 'cost-tally-backup', label: 'Tally Backup Explorer', path: '/costing/tally-backup', icon: 'file-search' },
    ]
  },
  {
    key: 'finance',
    label: 'Finance',
    defaultPath: '/pnl-dashboard',
    allowedRoles: [999],
    allowedPermissions: ['costing.view'],
    subNav: [
      { key: 'fin-pnl', label: 'P&L Dashboard', path: '/pnl-dashboard', icon: 'line-chart' },
      { key: 'fin-categories', label: 'Cost Categories', path: '/cost-categories', icon: 'setting' },
      { key: 'fin-overheads', label: 'Monthly Overheads', path: '/monthly-overheads', icon: 'bank' },
      { key: 'fin-costing', label: 'Product Costing', path: '/temp-costing', icon: 'calculator' },
    ]
  },
  {
    key: 'access-control',
    label: 'Access Control',
    defaultPath: '/access-control',
    allowedRoles: [5, 999],
    allowedPermissions: ['users.view', 'roles.view'],
    subNav: [
      {
        key: 'access-control-console',
        label: 'Users & Roles',
        path: '/access-control',
        icon: 'safety',
        allowedPermissions: ['users.view', 'roles.view']
      }
    ]
  }
]

/**
 * Where each role lands after login. Single source of truth — used by the
 * post-login redirect in StackNavigation. Keep in sync with the sections
 * above when adding a role.
 */
export const roleLandingPaths = {
  999: '/admin-dashboard',
  5: '/admin-dashboard',
  7: '/sales-coordinator-dashboard',
  3: '/entry-dashboard',
  6: '/production-dashboard',
  4: '/admin-daily-entry-dealers',
  // Roles 1/2 have no dedicated dashboard; sales-coordinator-dashboard is
  // the one route their allowedRoles include.
  1: '/sales-coordinator-dashboard',
  2: '/sales-coordinator-dashboard',
  8: '/purchase/indents',
  9: '/purchase/requisitions',
  10: '/purchase/indents',
}

export function getLandingPath(roleId, permissions = []) {
  const id = Number(roleId)
  if (id === 5 && !permissions.includes('sales.overview.view')) {
    return '/admin-daily-entry-dealers'
  }
  return roleLandingPaths[id]
}

/**
 * Find which primary section and sub-nav item is active based on the current path
 */
export function getActiveNav(pathname) {
  for (const section of topNavSections) {
    for (const item of section.subNav) {
      if (pathname === item.path || pathname.startsWith(item.path + '/')) {
        return { section: section.key, subNavKey: item.key }
      }
    }
  }

  // Check defaultPaths as fallback
  for (const section of topNavSections) {
    if (pathname === section.defaultPath) {
      return { section: section.key, subNavKey: section.subNav[0]?.key }
    }
  }

  return { section: null, subNavKey: null }
}

/**
 * Filter sections based on user role.
 * Sub-nav items may carry their own allowedRoles when the route behind them is
 * stricter than the section (e.g. /admin-dashboard requires its own permission
 * while Sales is visible to 3/4/5 too). The section's defaultPath resolves to the
 * first sub-nav item the role can actually open, so clicking a section never
 * lands on /unauthorized.
 */
export function getSectionsForRole(roleId, permissions = []) {
  const id = Number(roleId)
  const permissionSet = new Set(permissions)
  const isAllowed = item =>
    item.allowedRoles?.includes(id) ||
    item.allowedPermissions?.some(permission => permissionSet.has(permission))

  return topNavSections
    .filter(isAllowed)
    .map(section => {
      const subNav = section.subNav.filter(
        item =>
          (!item.allowedRoles && !item.allowedPermissions) ||
          isAllowed(item)
      )
      return { ...section, subNav, defaultPath: subNav[0]?.path || section.defaultPath }
    })
    .filter(section => section.subNav.length > 0)
}
